import withI18n from 'web/utils/withTranslations'
import Text, { TextSizes } from '../../../ui/typography/text'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { FormEvent, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { showToastError } from '../../../messages'
import { useAcceptInvite } from '@dolfin/hooks/auth'
import posthog from 'posthog-js'
import { Button } from '../../../stories/Button'
import Heading, {
  HeadingSizes,
  HeadingWeights
} from '../../../ui/typography/heading'
import { TextField } from '../../../stories/TextField'
import InviteExpired from './InviteExpired'

const FormSignup = () => {
  const [password, setPassword] = useState('')
  const [isClient, setIsClient] = useState(false)
  const [isInvitation, setIsInvitation] = useState(false)
  const router = useRouter()

  useEffect(() => {
    setIsClient(true)
    setIsInvitation(router.query['invitation'] === 'true')
  }, [router.query])

  const acceptInvite = useAcceptInvite(
    () => {
      posthog.capture('user_signed_up')
      router.push('/login')
    },
    error => {
      posthog.captureException(error)
      showToastError(error)
    }
  )

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (password.length < 8) {
      showToastError(new Error(t`The password must be at least 8 characters.`))
      return
    }

    if (!/(?=.*[A-Z])(?=.*\d).+/.test(password)) {
      showToastError(
        new Error(
          t`The password must contain at least one uppercase letter and one number.`
        )
      )
      return
    }

    if (/\s/.test(password)) {
      showToastError(
        new Error(t`The password cannot contain whitespace characters.`)
      )
      return
    }

    const hashParams = router.asPath.includes('#')
      ? router.asPath.split('#')[1]
      : ''
    const parsedParams = hashParams
      ? Object.fromEntries(
          hashParams.split('&').map(param => {
            const [key, value] = param.split('=')
            return [key, value]
          })
        )
      : {}

    const formData = Object.fromEntries(
      new FormData(e.currentTarget).entries()
    ) as { password: string }

    acceptInvite.mutate({
      accessToken: parsedParams['access_token'],
      refreshToken: parsedParams['refresh_token'],
      password: formData.password
    })
  }

  if (!isInvitation) {
    return null
  }

  if (isClient && router.asPath.includes('error_code=otp_expired')) {
    return <InviteExpired />
  }

  return (
    <div className='flex flex-col gap-6'>
      <Heading
        level={2}
        size={HeadingSizes['2XL']}
        weight={HeadingWeights.FONT_MEDIUM}>
        <Trans>Set up your password</Trans>
      </Heading>
      <form
        action='#'
        className='flex flex-col gap-6 w-96'
        onSubmit={handleSubmit}
        method='POST'>
        <Text size={TextSizes.SM} color='text-primary/70'>
          <Trans>
            Your new password must be at least 8 characters <br />
            and have one uppercase letter and one number.
          </Trans>
        </Text>
        <div>
          <TextField
            type='password'
            id='password'
            name='password'
            placeholder={t`Password` + '*'}
            value={password}
            onChange={e => setPassword(e)}
          />
        </div>
        <div className={'flex flex-col items-center w-full'}>
          <Button
            className={'w-full justify-center'}
            type='submit'
            label={t`Sign up`}
          />
        </div>
      </form>
    </div>
  )
}

export const getServerSideProps = withI18n(ctx => {
  return {
    props: { translation: ctx.translation, locale: ctx.locale }
  }
})

export default FormSignup
